/**
 * SmartstoreLinks — 스마트스토어 상품 상세에 박을 nid.naver.com 진입 링크 관리
 *
 * fiket 도메인은 스마트스토어 상품 상세에서 차단되므로
 * 반드시 이 페이지에서 생성한 링크를 사용해야 한다.
 */

import { useCallback } from 'react'
import { useApiFetch } from '../hooks/useApiFetch'
import { useToast } from '../components/ToastContainer'
import { copySmartstoreLinkToClipboard, type SmartstoreTo } from '../lib/smartstoreLink'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import type { ThankYouDesign } from '../lib/thankyou-types'
import styles from './SmartstoreLinks.module.css'

interface Template {
  id: string
  name: string
  slug: string
  category: string
  tier: string
  thumbnailUrl: string | null
  cachedHtml: string | null
  isActive: boolean
}

interface TemplatesResponse {
  templates: Template[]
}

interface Section {
  key: SmartstoreTo
  title: string
  description: string
  path: string
}

const COMMON_SECTIONS: Section[] = [
  {
    key: 'studio',
    title: 'AI 스튜디오',
    description: '로그인 후 /studio 페이지로 이동. AI 사진 생성을 시작하는 진입점',
    path: '/studio',
  },
  {
    key: 'create',
    title: '청첩장 제작 바로가기',
    description: '첫 번째 활성 템플릿으로 에디터(신규 모드) 직행. 구매 직후 즉시 제작 시작',
    path: '/invitations/new?templateId=<첫번째 활성 템플릿>',
  },
  {
    key: 'invitations',
    title: '템플릿 선택 페이지',
    description: '로그인 후 /invitations 에 랜딩. 전체 템플릿 목록에서 사용자가 고를 수 있음',
    path: '/invitations',
  },
  {
    key: 'thankyou',
    title: '감사장 페이지',
    description: '로그인 후 /thanks 감사장 갤러리에 랜딩. 전체 감사장 디자인을 보고 고를 수 있음',
    path: '/thanks',
  },
  {
    key: 'thankyou-create',
    title: '감사장 제작 바로가기',
    description: '첫 번째 활성 감사장 디자인으로 카드 생성 후 에디터 직행. 구매 직후 즉시 제작 시작',
    path: '/thanks/new',
  },
]

export function SmartstoreLinks() {
  const { addToast } = useToast()
  const { data, isLoading } = useApiFetch<TemplatesResponse>('/api/admin/templates')
  const templates = data?.templates ?? []
  const activeTemplates = templates.filter((t) => t.isActive)

  const { data: thankYouDesigns, isLoading: thankYouLoading } = useApiFetch<ThankYouDesign[]>('/api/admin/thankyou-designs')
  const activeThankYouDesigns = (thankYouDesigns ?? []).filter((d) => d.status === 'ACTIVE')

  const handleCopy = useCallback(async (to: SmartstoreTo, id?: string, label?: string) => {
    try {
      await copySmartstoreLinkToClipboard(to, id)
      addToast({ type: 'success', message: `${label ?? '스토어 링크'} 복사됨` })
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : '스토어 링크 생성 실패',
      })
    }
  }, [addToast])

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>스마트스토어 진입 링크</h2>
        <p className={styles.subtitle}>
          스마트스토어 상품 상세는 <code>.fiket.wedding</code> / <code>.fiket.ai</code> 외부 도메인을 차단합니다.
          반드시 이 페이지에서 생성한 <code>nid.naver.com</code> 링크를 복사해서 사용하세요.
        </p>
      </div>

      {/* 공통 섹션 */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>공통 진입 링크</h3>
        <div className={styles.commonGrid}>
          {COMMON_SECTIONS.map((s) => (
            <div key={s.key} className={styles.commonCard}>
              <div className={styles.commonHead}>
                <span className={styles.commonName}>{s.title}</span>
                <Badge color="blue">{s.key}</Badge>
              </div>
              <p className={styles.commonDesc}>{s.description}</p>
              <code className={styles.commonPath}>{s.path}</code>
              <Button
                variant="primary"
                onClick={() => handleCopy(s.key, undefined, s.title)}
              >
                링크 복사
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* 템플릿별 미리보기 링크 */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          개별 템플릿 미리보기
          <span className={styles.sectionHint}>
            로그인 후 <code>/invitations?preview=&lt;id&gt;</code>에 랜딩 + 프리뷰 모달 자동 오픈
          </span>
        </h3>
        {isLoading ? (
          <div className={styles.loading}><Spinner /></div>
        ) : activeTemplates.length === 0 ? (
          <div className={styles.empty}>활성 템플릿이 없습니다.</div>
        ) : (
          <div className={styles.templateGrid}>
            {activeTemplates.map((t) => (
              <div key={t.id} className={styles.templateCard}>
                <div className={styles.templatePreview}>
                  {t.thumbnailUrl ? (
                    <img src={t.thumbnailUrl} alt={t.name} />
                  ) : t.cachedHtml ? (
                    <iframe
                      srcDoc={t.cachedHtml}
                      title={t.name}
                      className={styles.templateIframe}
                    />
                  ) : (
                    <div className={styles.templatePlaceholder}>{t.name}</div>
                  )}
                </div>
                <div className={styles.templateInfo}>
                  <span className={styles.templateName}>{t.name}</span>
                  <Button
                    variant="outline"
                    onClick={() => handleCopy('template', t.id, `"${t.name}" 링크`)}
                  >
                    링크 복사
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 감사장 디자인별 미리보기 링크 */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          개별 감사장 미리보기
          <span className={styles.sectionHint}>
            로그인 후 <code>/thanks?preview=&lt;id&gt;</code>에 랜딩 + 프리뷰 모달 자동 오픈
          </span>
        </h3>
        {thankYouLoading ? (
          <div className={styles.loading}><Spinner /></div>
        ) : activeThankYouDesigns.length === 0 ? (
          <div className={styles.empty}>활성 감사장 디자인이 없습니다.</div>
        ) : (
          <div className={styles.templateGrid}>
            {activeThankYouDesigns.map((d) => (
              <div key={d.id} className={styles.templateCard}>
                <div className={styles.templatePreview}>
                  {d.thumbnailUrl ? (
                    <img src={d.thumbnailUrl} alt={d.name} />
                  ) : (
                    <div className={styles.templatePlaceholder}>{d.name}</div>
                  )}
                </div>
                <div className={styles.templateInfo}>
                  <span className={styles.templateName}>{d.name}</span>
                  <Button
                    variant="outline"
                    onClick={() => handleCopy('thankyou-design', d.id, `"${d.name}" 감사장 링크`)}
                  >
                    링크 복사
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
